import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import IconLabel from '@/components/molecules/icon-label/icon-label';
import MediaLightbox, {
  MediaSlide,
} from '@/components/molecules/media-lightbox/media-lightbox';
import { useI18n } from '@/libraries/contexts/i18n-context';
import {
  ROOM_PLACEHOLDER_IMAGE_URL,
  RoomMatrixRoomProps,
} from '../room-matrix-room/room-matrix-room';
import RoomMatrixRoomBeds from '../room-matrix-room-beds/room-matrix-room-beds';
import styles from './room-matrix-room-details.module.scss';

interface RoomMatrixRoomDetailsProps {
  /** Whether the lightbox is open or not. */
  isOpen: boolean;
  /** The callback to handle the close event. */
  onClose: () => void;
  /** The room to display in the lightbox. */
  room: RoomMatrixRoomProps;
}

const RoomMatrixRoomDetails = ({
  isOpen,
  room,
  onClose,
}: RoomMatrixRoomDetailsProps) => {
  const {
    adultBeds,
    childrenBeds,
    description,
    extraBeds,
    facilitiesInRoom,
    name,
    personsNo,
  } = room;
  const { t } = useI18n();
  const pictures: RoomMatrixRoomProps['pictures'] =
    room.pictures.length > 0
      ? room.pictures
      : [
          {
            description: '',
            focalPoint: {
              leftPercentage: 50,
              topPercentage: 50,
            },
            isMain: true,
            longDescription: '',
            pictureId: 0,
            roomTypeIds: [0],
            url: ROOM_PLACEHOLDER_IMAGE_URL,
          },
        ];

  const items: MediaSlide[] = pictures.map(picture => ({
    media: {
      captionText: picture.description,
      id: `${picture.pictureId}`,
      image: {
        altText: picture.description || '',
        url: picture.url || ROOM_PLACEHOLDER_IMAGE_URL,
      },
    } as Content.Properties.ImageAndVideoGridItem,
    sideContent: (
      <div className={styles.component}>
        <Typography className={styles.heading} tagStyle="bodyLargeBold">
          {name}
        </Typography>

        {description && (
          <RichText className={styles.content} text={description} />
        )}

        <div className={styles.room}>
          <Typography className={styles.heading} tagStyle="bodyMediumBold">
            {t('items.modules.roomMatrix.facilitiesInRoom')}
          </Typography>

          <RoomMatrixRoomBeds
            adultBeds={adultBeds}
            childrenBeds={childrenBeds}
            extraBeds={extraBeds}
            personsNo={personsNo}
          />

          <div className={styles.facilities}>
            {facilitiesInRoom.map((facility, facilityIndex) => (
              <div key={facilityIndex} className={styles.tag}>
                <IconLabel text={facility} />
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  }));

  return <MediaLightbox isOpen={isOpen} items={items} onClose={onClose} />;
};

RoomMatrixRoomDetails.displayName = 'RoomMatrixRoomDetails';

export default RoomMatrixRoomDetails;
